import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import {
  Grid,
  FormControlLabel,
  Checkbox,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField
} from "@mui/material";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import PriceDisplay from "components/PriceDisplay";
import iaasService from "services/iaasService";
import Swal from "sweetalert2";
import { AuthContext } from "context";

function S3StorageConfig({ onBack }) {
  const { user } = useContext(AuthContext);
  const [storageSize, setStorageSize] = useState("100");
  const [redundancyLevel, setRedundancyLevel] = useState("single");
  const [cdnEnabled, setCdnEnabled] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [totalPrice, setTotalPrice] = useState(0);
  const [originalPrice, setOriginalPrice] = useState(null);
  const [discountApplied, setDiscountApplied] = useState(false);

  const showSuccessNotification = (message) => {
    Swal.fire({
      icon: 'success',
      title: 'Erfolg!',
      text: message,
      confirmButtonColor: '#3085d6',
      confirmButtonText: 'OK'
    });
  };

  const showErrorNotification = (message) => {
    Swal.fire({
      icon: 'error',
      title: 'Fehler',
      text: message,
      confirmButtonColor: '#d33',
      confirmButtonText: 'OK'
    });
  };

  // Berechne den Preis mit Berücksichtigung des Partner-Status
  useEffect(() => {
    const config = {
      storageSize,
      redundancyLevel,
      cdnEnabled
    };
    
    // Berechne den Basispreis
    const basePrice = iaasService.calculateS3StoragePrice(config);
    
    const isPartner = user?.isPartner === true;

    if (isPartner) {
      // 20% Rabatt für Partner
      setOriginalPrice(basePrice);
      setTotalPrice(basePrice * 0.8);
      setDiscountApplied(true);
    } else {
      setTotalPrice(basePrice);
      setOriginalPrice(null);
      setDiscountApplied(false);
    }
  }, [storageSize, redundancyLevel, cdnEnabled, user?.isPartner]);

  const handleOrder = async () => {
    if (!agreedToTerms) {
      showErrorNotification("Bitte stimmen Sie den AGBs zu.");
      return;
    }

    try {
      const configuration = {
        storageSize,
        redundancyLevel,
        cdnEnabled
      };
      
      // Füge Rabattinformationen hinzu, wenn ein Rabatt angewendet wurde
      if (discountApplied) {
        configuration.priceInfo = {
          originalPrice: originalPrice,
          discountApplied: true
        };
      }

      const response = await iaasService.orderService(
        's3-storage',
        configuration
      );

      if (response) {
        showSuccessNotification("Ihre Bestellung wurde erfolgreich aufgegeben!");
        setTimeout(() => {
          window.location.href = "/iaas/server";
        }, 2000);
      }
    } catch (error) {
      console.error("Fehler bei der Bestellung:", error);
      showErrorNotification("Es gab ein Problem bei der Bestellung. Bitte versuchen Sie es später erneut.");
    }
  };

  return (
    <SoftBox>
      <SoftBox p={3} border="1px solid #e9ecef" borderRadius="lg" bgcolor="#f8f9fa">
        <SoftTypography variant="h4" fontWeight="medium" color="dark" mb={3}>
          UDS S3 Storage Konfiguration
        </SoftTypography>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <SoftBox mb={3} p={3} border="1px solid #e9ecef" borderRadius="lg">
              <SoftTypography variant="h6" mb={2}>Speicherplatz (GB)</SoftTypography>
              <TextField
                fullWidth
                type="number"
                value={storageSize}
                onChange={(e) => setStorageSize(e.target.value)}
                helperText="Mindestens 100 GB"
                inputProps={{ min: 500 }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': {
                      borderColor: '#8b0000'
                    }
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#8b0000'
                  }
                }}
              />
            </SoftBox>
          </Grid>

          <Grid item xs={12} md={6}>
            <SoftBox mb={3} p={3} border="1px solid #e9ecef" borderRadius="lg">
              <SoftTypography variant="h6" mb={2}>Redundanz-Level</SoftTypography>
              <FormControl fullWidth>
                <Select
                  value={redundancyLevel}
                  onChange={(e) => setRedundancyLevel(e.target.value)}
                  sx={{
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#8b0000',
                    }
                  }}
                >
                  <MenuItem value="single">Single (Standard)</MenuItem>
                  <MenuItem value="double">Double (+50%)</MenuItem>
                  <MenuItem value="triple">Triple (+100%)</MenuItem>
                </Select>
              </FormControl>
            </SoftBox>
          </Grid>

          <Grid item xs={12}>
            <SoftBox p={3} border="1px solid #e9ecef" borderRadius="lg" bgcolor="#f8f9fa">
              <PriceDisplay 
                price={totalPrice} 
                originalPrice={originalPrice} 
                discountApplied={discountApplied} 
              />
            </SoftBox>
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  sx={{
                    color: '#8b0000',
                    '&.Mui-checked': {
                      color: '#8b0000',
                    }
                  }}
                />
              }
              label={
                <SoftTypography variant="body2">
                  Ich akzeptiere die{" "}
                  <a href="/agb" target="_blank" rel="noopener noreferrer" style={{ color: '#8b0000' }}>
                    AGBs
                  </a>{" "}
                  und bestelle den S3 Storage kostenpflichtig.
                </SoftTypography>
              }
            />
          </Grid>

          <Grid item xs={12}>
            <SoftBox display="flex" justifyContent="space-between">
              <Button 
                variant="outlined"
                onClick={onBack}
                sx={{
                  borderColor: '#8b0000',
                  color: '#8b0000',
                  '&:hover': {
                    borderColor: '#660000',
                    backgroundColor: 'rgba(139,0,0,0.04)'
                  }
                }}
              >
                Zurück
              </Button>
              <Button
                variant="contained"
                onClick={handleOrder}
                disabled={!agreedToTerms}
                sx={{
                  backgroundColor: '#8b0000',
                  '&:hover': {
                    backgroundColor: '#660000'
                  },
                  '&.Mui-disabled': {
                    backgroundColor: '#cccccc'
                  }
                }}
              >
                Jetzt bestellen
              </Button>
            </SoftBox>
          </Grid>
        </Grid>
      </SoftBox>
    </SoftBox>
  );
}

S3StorageConfig.propTypes = {
  onBack: PropTypes.func.isRequired,
};

export default S3StorageConfig;